<script setup lang="ts">
import {
    onBeforeMount,
    onBeforeUnmount,
    ref,
} from 'vue';
import { useSchoolStore } from '@/stores/school';
import type { FlightSchoolForm } from '@/types/FlightSchoolForm';
import SchoolForm from '@/components/AdminCenter/SchoolForm.vue';
import BreadCrumbs from '@/components/Utilities/BreadCrumbs.vue';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import router from '@/router';
import AdminRepository from '@/repositories/AdminRepository';

/* eslint-disable-next-line no-spaced-func, func-call-spacing */
const emit = defineEmits<{
    (event: 'update:info-visible', value: boolean): void;
}>();

const schoolStore = useSchoolStore();
if (!schoolStore?.school?.courses) {
    router.push('/admin/schools');
    throw new DOMException('No school selected');
}
const { school } = schoolStore;
const schoolId = school.id;
const loading = ref(true);

const formData = ref<FlightSchoolForm>({
    name: school.name,
    address: {
        line1: school.address.line1,
        line2: school.address.line2,
        city: school.address.city,
        state: school.address.state,
        country: school.address.country,
        postcode: school.address.postcode,
    },
    phone: school.phone,
    certificateUuids: school.courses.map((course: FlightSchoolCourse) => course.certificate.uuid),
    courseAcronyms: [],
    email: '',
    isVisible: school.isVisible,
    isArchived: school.isArchived,
});

onBeforeMount(async () => {
    emit('update:info-visible', false);

    const admins = await AdminRepository.getSchoolAdmins(school);
    formData.value.email = admins.find((admin) => admin.id === school.owner)?.email || '';
    loading.value = false;
});

onBeforeUnmount(() => {
    emit('update:info-visible', true);
});
</script>
<template>
    <div
        id="edit-school"
        class="text-fsum-black"
    >
        <BreadCrumbs
            :bread-crumbs="[{label: 'School Directory', to: { name: 'schoolDirectory' }}]"
            current-page="Edit Flight School"
        />
        <header id="edit-school-header">
            <h1 class="q-pb-xl q-mb-xs fsum-generic-header">
                Edit School Information
            </h1>
        </header>
        <SchoolForm
            v-if="!loading"
            :form-data="formData"
            :button-label="'Save'"
            :original-id="schoolId"
        />
    </div>
</template>
<style scoped>
#edit-school {
    font-size: 16px;
    text-align: center;
}
</style>
